import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Badge, Button, Alert, Spinner, Form, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faEnvelope, faCheck, faTimes, faSync, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const EmailSchedulateManagement = () => {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [filter, setFilter] = useState('all'); // 'all', 'pending', 'sent', 'failed'
    const [stats, setStats] = useState(null);

    useEffect(() => {
        fetchEmails();
    }, [filter]);

    const fetchEmails = async () => {
        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const token = localStorage.getItem('access_token');
            let endpoint = `${API_URL}/api/scheduled-emails/all`;
            
            if (filter === 'pending') {
                endpoint = `${API_URL}/api/scheduled-emails/pending`;
            }
            
            const response = await axios.get(endpoint, {
                headers: { Authorization: `Bearer ${token}` },
                params: filter === 'all' ? { limit: 100 } : {}
            });
            
            let fetchedEmails = response.data || [];
            
            // Applica filtri client-side per 'sent' e 'failed'
            if (filter === 'sent') {
                fetchedEmails = fetchedEmails.filter(e => e.status === 'sent');
            } else if (filter === 'failed') {
                fetchedEmails = fetchedEmails.filter(e => e.status === 'failed');
            }
            
            setEmails(fetchedEmails);
            calculateStats(fetchedEmails);
        } catch (error) {
            console.error('Errore caricamento email schedulate:', error);
            setMessage({ 
                type: 'danger', 
                text: 'Errore nel caricamento delle email schedulate: ' + (error.response?.data?.detail || error.message)
            });
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (emailList) => {
        const pending = emailList.filter(e => e.status === 'pending').length;
        const sent = emailList.filter(e => e.status === 'sent').length;
        const failed = emailList.filter(e => e.status === 'failed').length;
        setStats({ total: emailList.length, pending, sent, failed });
    };

    const processEmails = async () => {
        if (!window.confirm('Vuoi processare tutte le email schedulate pronte per l\'invio?')) {
            return;
        }

        setProcessing(true);
        setMessage({ type: '', text: '' });
        
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post(
                `${API_URL}/api/scheduled-emails/process`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setMessage({ 
                type: 'success', 
                text: `Processo completato! Inviate: ${response.data.emails_sent}, Fallite: ${response.data.emails_failed}`
            });
            
            // Ricarica la lista dopo il processo
            setTimeout(() => fetchEmails(), 1000);
        } catch (error) {
            console.error('Errore processo email:', error);
            setMessage({ 
                type: 'danger', 
                text: 'Errore durante il processo: ' + (error.response?.data?.detail || error.message)
            });
        } finally {
            setProcessing(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: <Badge bg="warning" text="dark"><FontAwesomeIcon icon={faCalendarAlt} /> In Attesa</Badge>,
            sent: <Badge bg="success"><FontAwesomeIcon icon={faCheck} /> Inviata</Badge>,
            failed: <Badge bg="danger"><FontAwesomeIcon icon={faTimes} /> Fallita</Badge>
        };
        return badges[status] || <Badge bg="secondary">{status}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: dateString.includes('T') ? '2-digit' : undefined,
            minute: dateString.includes('T') ? '2-digit' : undefined
        });
    };

    const isReadyToSend = (email) => {
        if (email.status !== 'pending') return false;
        const sendDate = new Date(email.send_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return sendDate <= today;
    };

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                    Email Schedulate
                </h2>
                <Button 
                    variant="primary" 
                    onClick={processEmails}
                    disabled={processing || loading}
                >
                    {processing ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Processando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSync} className="me-2" />
                            Processa Email Pronte
                        </>
                    )}
                </Button>
            </div>

            {message.text && (
                <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
                    {message.text}
                </Alert>
            )}

            {stats && (
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h5>Totale</h5>
                                <h2>{stats.total}</h2>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center border-warning">
                            <Card.Body>
                                <h5>In Attesa</h5>
                                <h2 className="text-warning">{stats.pending}</h2>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center border-success">
                            <Card.Body>
                                <h5>Inviate</h5>
                                <h2 className="text-success">{stats.sent}</h2>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center border-danger">
                            <Card.Body>
                                <h5>Fallite</h5>
                                <h2 className="text-danger">{stats.failed}</h2>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <span>Lista Email Schedulate</span>
                        <Form.Select 
                            style={{ width: '200px' }}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        >
                            <option value="all">Tutte</option>
                            <option value="pending">In Attesa</option>
                            <option value="sent">Inviate</option>
                            <option value="failed">Fallite</option>
                        </Form.Select>
                    </div>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Caricamento email schedulate...</p>
                        </div>
                    ) : emails.length === 0 ? (
                        <Alert variant="info" className="text-center">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            Nessuna email schedulata trovata.
                        </Alert>
                    ) : (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Ordine</th>
                                    <th>Cliente</th>
                                    <th>Template</th>
                                    <th>Data Invio</th>
                                    <th>Stato</th>
                                    <th>Inviata il</th>
                                    <th>Errore</th>
                                </tr>
                            </thead>
                            <tbody>
                                {emails.map((email) => (
                                    <tr key={email.id} className={isReadyToSend(email) ? 'table-warning' : ''}>
                                        <td>{email.id}</td>
                                        <td>
                                            <strong>#{email.ordine_id}</strong>
                                            {email.email_data?.ordine_numero && (
                                                <div className="text-muted small">
                                                    {email.email_data.ordine_numero}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {email.email_data?.cliente_nome && (
                                                <div>
                                                    {email.email_data.cliente_nome} {email.email_data.cliente_cognome}
                                                </div>
                                            )}
                                            {email.email_data?.cliente_email && (
                                                <div className="text-muted small">
                                                    {email.email_data.cliente_email}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <code className="small">{email.template_type}</code>
                                        </td>
                                        <td>
                                            {formatDate(email.send_date)}
                                            {isReadyToSend(email) && (
                                                <Badge bg="info" className="ms-2">Pronta</Badge>
                                            )}
                                        </td>
                                        <td>{getStatusBadge(email.status)}</td>
                                        <td>{formatDate(email.sent_date)}</td>
                                        <td>
                                            {email.error_message && (
                                                <span className="text-danger small" title={email.error_message}>
                                                    <FontAwesomeIcon icon={faExclamationTriangle} /> 
                                                    {email.error_message.substring(0, 30)}...
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <Card className="mt-4 bg-light">
                <Card.Body>
                    <h5 className="mb-3">ℹ️ Informazioni</h5>
                    <ul className="mb-0">
                        <li>Le email vengono schedulate automaticamente quando un ordine dal sito web passa a stato "Concluso"</li>
                        <li>L'invio è programmato per <strong>15 giorni dopo</strong> il completamento dell'ordine</li>
                        <li>Clicca su <strong>"Processa Email Pronte"</strong> per inviare manualmente le email con data odierna o precedente</li>
                        <li>Per l'automazione completa, configura un cron job che chiami l'endpoint <code>/api/scheduled-emails/process</code> giornalmente</li>
                        <li>Le email inviate vengono automaticamente ripulite dopo 30 giorni</li>
                    </ul>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default EmailSchedulateManagement;
